import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Loader2, Mail, Pencil, Plus, Power, Send, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { errorText, formatDay, formatDateTime } from "@/lib/partnerApi";
import { PILL, UpdateRow, ReviewUpdateDialog, notifyPartners } from "@/components/admin/partners/PartnerUpdatesAdmin";

const FIELD = "w-full px-3 py-2 border border-stone-300 rounded text-sm";
const LABEL = "block text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1";
const BTN = "inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider disabled:opacity-40 transition-colors";
const SPECIALTIES = ["Eletricidade", "Canalização", "Pintura", "Carpintaria", "Pladur", "Pavimentos", "Serralharia", "Construção civil", "Climatização"];

function AccessChip({ p }) {
    if (!p.active) return <span className={`${PILL} bg-stone-200 text-stone-700`}>Inativo</span>;
    if (p.has_password) return <span className={`${PILL} bg-emerald-100 text-emerald-800`}>Ativo</span>;
    if (p.invite_expired) return <span className={`${PILL} bg-red-100 text-red-800`}>Convite expirado</span>;
    return <span className={`${PILL} bg-amber-100 text-amber-800`}>Convite pendente</span>;
}

export function PartnersManager() {
    const [items, setItems] = useState(null);
    const [form, setForm] = useState(null);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        api.get("/admin/partners").then(({ data }) => setItems(data)).catch(() => setItems([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="admin-partners-manager">
            <button type="button" onClick={() => setForm({})} className={`${BTN} bg-[hsl(var(--forest))] text-white mb-4`} data-testid="partner-create-open">
                <Plus className="w-4 h-4" /> Novo parceiro
            </button>
            {items === null ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : items.length === 0 ? (
                <p className="text-sm text-stone-400 italic" data-testid="partners-empty">Ainda não há parceiros.</p>
            ) : (
                <ul className="grid md:grid-cols-2 gap-2">
                    {items.map((p) => (
                        <li key={p.id}>
                            <button type="button" onClick={() => setOpenId(p.id)} className="w-full text-left bg-white rounded-lg border border-stone-200 p-4 hover:border-[hsl(var(--copper))] transition-colors" data-testid={`partner-row-${p.id}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-semibold text-stone-900 truncate">{p.company_name}</div>
                                        <div className="text-xs text-stone-500 truncate">{[p.contact_name, p.specialty].filter(Boolean).join(" · ")}</div>
                                    </div>
                                    <AccessChip p={p} />
                                </div>
                                <div className="text-xs text-stone-500 mt-2 truncate">{p.email}{p.phone && ` · ${p.phone}`}</div>
                                <div className="text-xs text-stone-600 mt-1">{p.active_obras} obra(s) atribuída(s){p.pending_updates > 0 && <> · <strong className="text-amber-800">{p.pending_updates} pendente(s)</strong></>}</div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <PartnerFormDialog initial={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />
            <PartnerDetailDialog pid={openId} onClose={() => setOpenId(null)} onChanged={() => { load(); notifyPartners(); }} />
        </div>
    );
}

function PartnerFormDialog({ initial, onClose, onSaved }) {
    const [v, setV] = useState({});
    const [busy, setBusy] = useState(false);
    useEffect(() => { setV(initial || {}); }, [initial]);
    const editing = !!initial?.id;
    const set = (k) => (e) => setV((cur) => ({ ...cur, [k]: e.target.value }));

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        const body = { company_name: v.company_name || "", contact_name: v.contact_name || "", email: v.email || "", phone: v.phone || "", specialty: v.specialty || "" };
        try {
            if (editing) {
                await api.patch(`/admin/partners/${initial.id}`, body);
                toast.success("Dados do parceiro atualizados.");
            } else {
                const { data } = await api.post("/admin/partners", body);
                if (data.invite_email_sent) toast.success("Parceiro criado. Enviámos o convite por email (válido 48 horas).");
                else toast.warning("Parceiro criado, mas o email do convite não foi enviado. Use «Reenviar convite» dentro de alguns minutos.");
            }
            onSaved();
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!initial} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="partner-form-dialog">
                <DialogHeader><DialogTitle>{editing ? "Editar parceiro" : "Novo parceiro"}</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-3">
                    <label className="block"><span className={LABEL}>Profissional ou empresa *</span><input value={v.company_name || ""} onChange={set("company_name")} className={FIELD} data-testid="partner-form-company" /></label>
                    <label className="block"><span className={LABEL}>Nome do responsável</span><input value={v.contact_name || ""} onChange={set("contact_name")} className={FIELD} data-testid="partner-form-contact" /></label>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block"><span className={LABEL}>Email *</span><input type="email" value={v.email || ""} onChange={set("email")} className={FIELD} data-testid="partner-form-email" /></label>
                        <label className="block"><span className={LABEL}>Telefone</span><input value={v.phone || ""} onChange={set("phone")} className={FIELD} data-testid="partner-form-phone" /></label>
                    </div>
                    <label className="block"><span className={LABEL}>Especialidade</span>
                        <input list="partner-specialties" value={v.specialty || ""} onChange={set("specialty")} className={FIELD} data-testid="partner-form-specialty" />
                        <datalist id="partner-specialties">{SPECIALTIES.map((s) => <option key={s} value={s} />)}</datalist>
                    </label>
                    {!editing && <p className="text-[11px] text-stone-500">O parceiro recebe um convite por email para definir a palavra-passe (válido 48 horas, uma única utilização).</p>}
                    <button type="submit" disabled={busy || !(v.company_name || "").trim() || !(v.email || "").trim()} className={`${BTN} bg-[hsl(var(--forest))] text-white`} data-testid="partner-form-save">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}{editing ? "Guardar" : "Criar e enviar convite"}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function PartnerDetailDialog({ pid, onClose, onChanged }) {
    const [d, setD] = useState(null);
    const [edit, setEdit] = useState(null);
    const [review, setReview] = useState(null);
    const [days, setDays] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        if (!pid) return;
        api.get(`/admin/partners/${pid}`).then(({ data }) => setD(data)).catch((e) => toast.error(errorText(e)));
    }, [pid]);
    useEffect(() => { setD(null); load(); }, [load]);
    const changed = () => { load(); onChanged(); };

    const run = async (key, fn, ok) => {
        setBusy(key);
        try { await fn(); toast.success(ok); changed(); } catch (e) { toast.error(errorText(e)); } finally { setBusy(null); }
    };
    const p = d?.partner;
    const resend = async () => {
        setBusy("invite");
        try {
            const { data } = await api.post(`/admin/partners/${p.id}/invite`);
            if (!data.invite_email_sent) toast.warning("O email não foi enviado (serviço de email indisponível). Tente novamente dentro de alguns minutos.");
            else toast.success(p.has_password ? "Enviámos um link para definir nova palavra-passe." : "Convite reenviado por email.");
            changed();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(null);
        }
    };
    const toggleActive = () => {
        if (p.active && !window.confirm(`Desativar ${p.company_name}? O acesso é bloqueado de imediato. O histórico mantém-se.`)) return;
        run("active", () => api.patch(`/admin/partners/${p.id}`, { active: !p.active }), p.active ? "Acesso desativado." : "Parceiro reativado.");
    };
    const unassign = (a) => {
        if (!window.confirm(`Retirar o acesso de ${p.company_name} à obra ${a.title}? O histórico fica guardado.`)) return;
        run(`un-${a.id}`, () => api.delete(`/admin/partner-assignments/${a.id}`), "Acesso à obra retirado.");
    };

    return (
        <Dialog open={!!pid} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="partner-detail-dialog">
                <DialogHeader><DialogTitle className="font-display text-xl text-[hsl(var(--forest))] pr-6">{p?.company_name || "Parceiro"}</DialogTitle></DialogHeader>
                {!d ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : (
                    <div className="space-y-5">
                        <section className="text-sm text-stone-700 space-y-1" data-testid="partner-detail-info">
                            <div className="flex items-center gap-2 flex-wrap"><AccessChip p={p} />{p.specialty && <span className="text-stone-500">{p.specialty}</span>}</div>
                            <div>{p.contact_name}</div>
                            <div className="text-stone-500">{p.email}{p.phone && ` · ${p.phone}`}</div>
                            <div className="text-[11px] text-stone-400">
                                {p.invited_at && `Convite enviado ${formatDateTime(p.invited_at)}`}{p.last_login_at && ` · Último acesso ${formatDateTime(p.last_login_at)}`}
                            </div>
                            <div className="flex gap-2 flex-wrap pt-2">
                                <button type="button" onClick={() => setEdit(p)} className={`${BTN} border border-stone-300 text-stone-700`} data-testid="partner-edit"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                                {p.active && (
                                    <button type="button" disabled={!!busy} onClick={resend} className={`${BTN} border border-stone-300 text-stone-700`} data-testid="partner-resend-invite">
                                        {busy === "invite" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} {p.has_password ? "Enviar link de nova palavra-passe" : "Reenviar convite"}
                                    </button>
                                )}
                                <button type="button" disabled={!!busy} onClick={toggleActive} className={`${BTN} border ${p.active ? "border-red-200 text-red-700" : "border-emerald-300 text-emerald-800"}`} data-testid="partner-toggle-active">
                                    <Power className="w-3.5 h-3.5" /> {p.active ? "Desativar acesso" : "Reativar"}
                                </button>
                            </div>
                        </section>
                        <section data-testid="partner-assignments">
                            <div className={LABEL}>Obras atribuídas</div>
                            <ul className="space-y-2">
                                {d.assignments.length === 0 && <li className="text-sm text-stone-400 italic">Sem obras atribuídas.</li>}
                                {d.assignments.map((a) => (
                                    <li key={a.id} className={`rounded-lg border p-3 text-sm ${a.active ? "border-stone-200 bg-white" : "border-stone-100 bg-stone-50 text-stone-500"}`} data-testid={`assignment-${a.id}`}>
                                        <div className="flex justify-between gap-2 flex-wrap">
                                            <div className="min-w-0"><div className="font-semibold">{a.title}</div><div className="text-xs text-stone-500">Cliente: {a.lead_name} · {a.lead_status}{a.site_location && ` · ${a.site_location}`}</div></div>
                                            {a.active ? (
                                                <div className="flex gap-1.5">
                                                    <button type="button" onClick={() => setDays(a.id)} className={`${BTN} !px-3 !py-1.5 border border-stone-300 text-stone-700`} data-testid={`assignment-days-${a.id}`}><CalendarDays className="w-3.5 h-3.5" /> Dias ({a.planned_days.length})</button>
                                                    <button type="button" disabled={!!busy} onClick={() => unassign(a)} className={`${BTN} !px-3 !py-1.5 border border-red-200 text-red-700`} data-testid={`assignment-remove-${a.id}`}><Trash2 className="w-3.5 h-3.5" /> Retirar</button>
                                                </div>
                                            ) : <span className="text-xs">Acesso retirado {formatDateTime(a.removed_at)}</span>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {p.active && <AssignForm pid={p.id} assigned={d.assignments.filter((a) => a.active).map((a) => a.lead_id)} onDone={changed} />}
                        </section>
                        <section data-testid="partner-history">
                            <div className={LABEL}>Histórico de atualizações ({d.updates.length})</div>
                            {d.updates.length === 0 ? <p className="text-sm text-stone-400 italic">Sem atualizações.</p> : (
                                <ul className="space-y-2">{d.updates.map((u) => <li key={u.id}><UpdateRow u={u} onOpen={setReview} /></li>)}</ul>
                            )}
                        </section>
                    </div>
                )}
                <PartnerFormDialog initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); changed(); }} />
                <ReviewUpdateDialog id={review} onClose={() => setReview(null)} onChanged={changed} />
                {days && <PlannedDaysDialog assignmentId={days} onClose={() => setDays(null)} onSaved={changed} />}
            </DialogContent>
        </Dialog>
    );
}

function AssignForm({ pid, assigned, onDone }) {
    const [obras, setObras] = useState([]);
    const [leadId, setLeadId] = useState("");
    const [label, setLabel] = useState("");
    const [location, setLocation] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/admin/partner-obras").then(({ data }) => setObras(data)).catch(() => {}); }, []);
    const options = obras.filter((o) => !assigned.includes(o.id));
    const chosen = obras.find((o) => o.id === leadId);

    const save = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/partners/${pid}/assignments`, { lead_id: leadId, label, site_location: location });
            toast.success("Obra atribuída. O parceiro foi avisado.");
            setLeadId(""); setLabel(""); setLocation("");
            onDone();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-3 rounded-lg border border-dashed border-stone-300 p-3 space-y-2" data-testid="assign-form">
            <div className={LABEL}>Atribuir obra (adjudicada ou em execução)</div>
            <select value={leadId} onChange={(e) => setLeadId(e.target.value)} className={FIELD} data-testid="assign-lead-select">
                <option value="">Escolher obra…</option>
                {options.map((o) => <option key={o.id} value={o.id}>{o.name} · {(o.work_types || []).join(", ")} · {o.location} · {o.status}</option>)}
            </select>
            {chosen && (
                <div className="grid sm:grid-cols-2 gap-2">
                    <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`Referência para o parceiro (ex.: ${(chosen.work_types || [])[0] || "Obra"} ${chosen.location || ""})`} className={FIELD} data-testid="assign-label" />
                    <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={`Localização para o parceiro (${chosen.location || "morada da obra"})`} className={FIELD} data-testid="assign-location" />
                </div>
            )}
            <p className="text-[11px] text-stone-500">O parceiro não vê o nome do cliente: vê a referência, a localização e o estado da obra.</p>
            <button type="button" onClick={save} disabled={!leadId || busy} className={`${BTN} bg-[hsl(var(--forest))] text-white`} data-testid="assign-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Atribuir
            </button>
        </div>
    );
}

function eachDay(from, to, weekdaysOnly) {
    const out = [];
    const d = new Date(`${from}T12:00:00`);
    const end = new Date(`${to}T12:00:00`);
    while (d <= end && out.length < 370) {
        const wd = d.getDay();
        if (!weekdaysOnly || (wd !== 0 && wd !== 6)) out.push(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 1);
    }
    return out;
}

export function PlannedDaysDialog({ assignmentId, onClose, onSaved }) {
    const [a, setA] = useState(null);
    const [planned, setPlanned] = useState([]);
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [weekdays, setWeekdays] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/admin/partner-assignments/${assignmentId}`).then(({ data }) => { setA(data); setPlanned(data.planned_days); }).catch((e) => toast.error(errorText(e)));
    }, [assignmentId]);

    const add = () => {
        if (!from) return;
        setPlanned((cur) => [...new Set([...cur, ...eachDay(from, to || from, weekdays)])].sort());
        setFrom(""); setTo("");
    };
    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/admin/partner-assignments/${assignmentId}`, { planned_days: planned });
            toast.success("Dias previstos guardados.");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="planned-days-dialog">
                <DialogHeader><DialogTitle>Dias de intervenção previstos</DialogTitle></DialogHeader>
                {!a ? <Loader2 className="w-5 h-5 animate-spin text-stone-400" /> : (
                    <div className="space-y-3 text-sm">
                        <p className="text-stone-600">{a.title} · Cliente: {a.lead_name}</p>
                        <div className="grid grid-cols-2 gap-2">
                            <label><span className={LABEL}>De</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={FIELD} data-testid="planned-from" /></label>
                            <label><span className={LABEL}>Até (opcional)</span><input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={FIELD} data-testid="planned-to" /></label>
                        </div>
                        <label className="flex items-center gap-2 text-xs text-stone-600"><input type="checkbox" checked={weekdays} onChange={(e) => setWeekdays(e.target.checked)} /> Só dias úteis (seg. a sex.)</label>
                        <button type="button" onClick={add} disabled={!from} className={`${BTN} border border-stone-300 text-stone-700`} data-testid="planned-add"><Plus className="w-3.5 h-3.5" /> Acrescentar</button>
                        <ul className="flex flex-wrap gap-1.5" data-testid="planned-list">
                            {planned.length === 0 && <li className="text-stone-400 italic text-xs">Sem dias previstos.</li>}
                            {planned.map((d) => (
                                <li key={d} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-[hsl(var(--forest))]/10 text-[hsl(var(--forest))] text-xs capitalize">
                                    {formatDay(d, { weekday: "short", day: "2-digit", month: "2-digit", year: "2-digit" })}
                                    <button type="button" onClick={() => setPlanned((cur) => cur.filter((x) => x !== d))} className="w-5 h-5 rounded-full hover:bg-white flex items-center justify-center" aria-label="Remover dia" data-testid={`planned-remove-${d}`}><X className="w-3 h-3" /></button>
                                </li>
                            ))}
                        </ul>
                        {a.worked_days.length > 0 && (
                            <p className="text-xs text-stone-500">Acrescentados pelo parceiro: {a.worked_days.map((d) => formatDay(d, { day: "2-digit", month: "2-digit" })).join(", ")}</p>
                        )}
                        <button type="button" onClick={save} disabled={busy} className={`${BTN} bg-[hsl(var(--forest))] text-white`} data-testid="planned-save">
                            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar dias
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
